import { FretboardModel } from '@fretgenius/core/fretboard-model';
import { INSTRUMENTS_BY_TYPE } from '@fretgenius/core/music-theory-service';

import { reportUiError } from '../../error/reporting';
import { FretboardConfigState } from '../types';
import { resolveValidTuningId, validateInstrumentDefaults } from './tuningUtils';

type SetFn = (
  partial:
    | FretboardConfigState
    | Partial<FretboardConfigState>
    | ((state: FretboardConfigState) => FretboardConfigState | Partial<FretboardConfigState>),
  replace?: false
) => void;

type GetFn = () => FretboardConfigState;

export function createFretboardActions(
  set: SetFn,
  get: GetFn,
  reportErrorFn: (error: unknown, context?: Record<string, unknown>) => void
): Pick<
  FretboardConfigState,
  | 'setAccidentalPreference'
  | 'setUseTrapezoidScaling'
  | 'setFretWidthBase'
  | 'setInstrumentType'
  | 'setTuningId'
  | 'setNumFrets'
  | 'setHeightRatio'
  | 'setUseEqualTemperament'
  | 'setNoteCircleScale'
  | 'setIsLeftHanded'
  | 'setShowFretMarkerLabels'
> {
  return {
    setAccidentalPreference: (preference) => set({ accidentalPreference: preference }),
    setUseTrapezoidScaling: (useTrapezoidScaling) => set({ useTrapezoidScaling }),
    setFretWidthBase: (width) => {
      const validWidth = Math.max(20, Math.min(100, width));
      set({ fretWidthBase: validWidth });
    },
    setInstrumentType: (instrumentType) => {
      const instrDef = INSTRUMENTS_BY_TYPE[instrumentType];
      if (!instrDef) {
        reportUiError(
          reportErrorFn,
          'fretboard.config.missingInstrumentDefaults',
          new Error('Instrument definition not found for selected type'),
          { stage: 'setInstrumentType', instrumentType }
        );
      } else {
        validateInstrumentDefaults(instrumentType, 'setInstrumentType', reportErrorFn);
      }

      const defaultTuningIdForInstrument = instrDef?.defaultTunings[0]?.id ?? '';

      set({
        instrumentType,
        tuningId: defaultTuningIdForInstrument,
      });

      set({
        fretboardModel: new FretboardModel({
          instrumentType,
          tuningId: defaultTuningIdForInstrument,
          numFrets: get().numFrets,
        }),
      });
    },
    setTuningId: (tuningId) => {
      const instrumentType = get().instrumentType;
      const validTuningId = resolveValidTuningId(
        instrumentType,
        tuningId,
        'setTuningId',
        reportErrorFn
      );
      set({ tuningId: validTuningId });
      set({
        fretboardModel: new FretboardModel({
          instrumentType,
          tuningId: validTuningId,
          numFrets: get().numFrets,
        }),
      });
    },
    setNumFrets: (numFrets) => {
      set({ numFrets });
      set({
        fretboardModel: new FretboardModel({
          instrumentType: get().instrumentType,
          tuningId: get().tuningId,
          numFrets,
        }),
      });
    },
    setHeightRatio: (heightRatio) => {
      const validHeightRatio = Math.max(0.15, Math.min(1, heightRatio));
      set({ heightRatio: validHeightRatio });
    },
    setUseEqualTemperament: (useEqualTemperament: boolean) => set({ useEqualTemperament }),
    setNoteCircleScale: (scale) => {
      const validScale = Math.max(0.4, Math.min(1.0, scale));
      set({ noteCircleScale: validScale });
    },
    setIsLeftHanded: (isLeftHanded) => set({ isLeftHanded }),
    setShowFretMarkerLabels: (show) => set({ showFretMarkerLabels: show }),
  };
}
